import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Global error boundary. Wraps the app root so a render error in a feature
 * doesn't white-screen the whole console (Saurava lacks this — see Phase 1 §2.10).
 * In production, hook `componentDidCatch` up to Sentry / whatever telemetry.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(error, () => {
        this.setState({ error: null })
      })
    }

    return (
      <div className="bg-paper-0 flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-ink-900 text-2xl font-semibold">Something went wrong.</h1>
        <p className="text-ink-600 max-w-md text-sm">
          The console hit an unexpected error and could not recover. Try reloading. If it keeps
          happening, share the error id below.
        </p>
        <code className="bg-paper-100 text-ink-700 rounded-md px-3 py-2 font-mono text-xs">
          {error.name}: {error.message}
        </code>
        <button
          type="button"
          onClick={() => {
            window.location.reload()
          }}
          className="bg-brand-500 hover:bg-brand-600 rounded-md px-4 py-2 text-sm font-semibold text-white"
        >
          Reload
        </button>
      </div>
    )
  }
}
